import React from "react";
import { toast } from "sonner";
import { Check, Crown, Zap, FileSearch, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/apiClient";

const TIERS = [
  {
    name: "VÉRIFICATION EXPRESS", price: "49€", period: "par devis", Icon: Zap,
    badge: "Idéal petit chantier", popular: false, testId: "pricing-card-express-btn",
    description: "Analyse rapide de votre devis pour éviter les pièges avant signature.",
    features: ["Cohérence des prix au m²", "Détection des oublis visibles", "Vérification des anomalies", "Rapport synthétique sous 24h", "Score de fiabilité / 100"],
    cta: "Choisir Express",
  },
  {
    name: "ANALYSE TECHNIQUE APPROFONDIE", price: "149€", period: "par devis", Icon: FileSearch,
    badge: "LE PLUS POPULAIRE", popular: true, testId: "pricing-card-technique-btn",
    description: "Analyse complète ligne à ligne par un expert métreur/ingénieur bâtiment.",
    features: ["Avis d'expert métreur", "Contrôle des quantitatifs", "Conformité matériaux & DTU", "Décennales vérifiées", "Optimisation coûts -10 à -25%", "Prestations manquantes"],
    cta: "Commander l'Analyse",
  },
  {
    name: "ACCOMPAGNEMENT PREMIUM", price: "Sur devis", period: "dès 390€", Icon: Crown,
    badge: "Sérénité Totale", popular: false, testId: "pricing-card-premium-btn",
    description: "Suivi personnalisé de bout en bout par un conducteur de travaux dédié.",
    features: ["Analyse jusqu'à 4 devis", "Aide à la négociation", "Sélection d'artisans certifiés", "Suivi des jalons & visites", "Assistance prioritaire 7j/7", "Protection juridique"],
    cta: "Demander un accompagnement",
  },
];

export const Pricing = () => {
  const choose = async (tier) => {
    try {
      await api.post("/leads", { name: "Prospect", email: "prospect@idbatplus.fr", message: `Intéressé par l'offre ${tier.name}`, plan: tier.name, project_type: "pricing" });
    } catch (err) {
      console.error("Lead submission failed:", err);
    }
    toast.success(`Offre « ${tier.name} » sélectionnée. Un expert vous contacte pour finaliser.`);
  };

  return (
    <section id="tarifs" className="py-24 bg-[#F5F7FA]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#0D6EFD] mb-3">Accompagnement Premium Devis</p>
          <h2 className="text-3xl sm:text-4xl font-extrabold text-[#0A0F1A] tracking-tight">
            Ne signez plus un devis sans l'avis d'un expert.
          </h2>
          <p className="mt-4 text-[#5A677D]">
            Trois formules claires pour sécuriser votre budget travaux et éviter les mauvaises surprises.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6 items-stretch">
          {TIERS.map((t) => (
            <div
              key={t.name}
              className={`relative rounded-3xl p-8 flex flex-col transition-transform hover:-translate-y-1 ${
                t.popular ? "bg-[#0A0F1A] text-white shadow-2xl shadow-[#0D6EFD]/20 md:scale-105" : "bg-white border border-[#0A0F1A]/10"
              }`}
            >
              {t.popular && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#0D6EFD] text-white text-xs font-semibold px-4 py-1 rounded-full flex items-center gap-1">
                  <Star className="w-3 h-3 fill-white" /> {t.badge}
                </span>
              )}
              <div className={`w-11 h-11 rounded-xl flex items-center justify-center mb-4 ${t.popular ? "bg-[#0D6EFD]/20" : "bg-[#0D6EFD]/10"}`}>
                <t.Icon className={`w-5 h-5 ${t.popular ? "text-[#00C3A7]" : "text-[#0D6EFD]"}`} />
              </div>
              <h3 className={`text-sm font-bold uppercase tracking-wide ${t.popular ? "text-white" : "text-[#0A0F1A]"}`}>{t.name}</h3>
              {!t.popular && <p className="text-xs text-[#0D6EFD] font-medium mt-1">{t.badge}</p>}
              <p className={`text-sm mt-3 ${t.popular ? "text-white/60" : "text-[#5A677D]"}`}>{t.description}</p>
              <div className="mt-5 flex items-end gap-1">
                <span className="text-4xl font-extrabold">{t.price}</span>
                <span className={`text-sm mb-1 ${t.popular ? "text-white/50" : "text-[#5A677D]"}`}>{t.period}</span>
              </div>
              <ul className="mt-6 space-y-2.5 flex-1">
                {t.features.map((f) => (
                  <li key={f} className={`flex gap-2 text-sm ${t.popular ? "text-white/80" : "text-[#0A0F1A]/80"}`}>
                    <Check className={`w-4 h-4 shrink-0 mt-0.5 ${t.popular ? "text-[#00C3A7]" : "text-[#0D6EFD]"}`} /> {f}
                  </li>
                ))}
              </ul>
              <Button
                data-testid={t.testId}
                onClick={() => choose(t)}
                className={`mt-8 rounded-full h-12 font-semibold ${
                  t.popular ? "bg-[#0D6EFD] hover:bg-[#0b5ed7] text-white" : "bg-[#0A0F1A] hover:bg-[#0A0F1A]/90 text-white"
                }`}
              >
                {t.cta}
              </Button>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
